import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Modal,
  DatePicker,
  Row,
  Col,
  Select,
  Input,
  Button,
  Form,
  Checkbox,
  AutoComplete,
  Switch,
  Upload,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { getErrorMessage } from "src/utils/errors";
import stateList from "src/data/states.json";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { SessionType } from "src/modules/interface";
import { logEvent } from "@src/appV2/lib/analytics";
import { ADMIN_EVENTS } from "src/constants/analyticsEvents";
import { LICENSE_STATUSES, ICreateLicense, IUpdateLicense, ILicenseFormProps } from "./interfaces";
import { addLicense, updateLicense, uploadVerificationDoc } from "./api";
import { LicenseStatusButtonGroup } from "./licenseStatusButtonGroup";
import {
  LICENSE_DETAILS_INIT,
  STATUS_CHANGE_ALERT_TITLES,
  LICENSE_STATUSES_VALUE_MAPPING,
  LICENSE_REJECTION_REASONS,
  DUPLICATE_LICENSE_INFO_TEXT,
} from "./constants";
import "./styles.scss";
import { LicenseDocPreview } from "./licenseDocPreview";
import { getConfirmationModalText, formatLicenseDates } from "./utils";
import { NurseWorkerType } from "@src/appV2/redesign/utils/WorkerTypes";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";

const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = Input;
const anyStateOption = { name: "Any", key: "Any" };

function RejectionReasonForm({ license, handleLicenseUpdate, disableForm }) {
  const [rejectionForm] = Form.useForm();
  useEffect(() => {
    rejectionForm.setFieldsValue(license);
  }, [license, rejectionForm]);

  return (
    <Form
      layout="vertical"
      name="licenseRejectionForm"
      form={rejectionForm}
      requiredMark={false}
      validateTrigger="onBlur"
      validateMessages={{ required: "" }}
      className="license-rejection-reason-form"
    >
      <Form.Item
        name="rejectionReason"
        label="Reason for Rejection"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <AutoComplete
          disabled={disableForm}
          options={Object.values(LICENSE_REJECTION_REASONS).map((rejectionReason) => ({
            value: rejectionReason,
          }))}
          value={license?.rejectionReason}
          placeholder="Reason"
          filterOption={(inputValue, option) =>
            option?.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
          allowClear={true}
          onChange={(reason) => {
            handleLicenseUpdate("rejectionReason", reason);
          }}
          data-testid="license-rejection-reason"
        />
      </Form.Item>
      <Form.Item name="rejectionNoteForWorker" label="Note to Worker">
        <TextArea
          data-testid="license-rejection-note-to-worker"
          rows={4}
          disabled={!license?.notifyRejection || disableForm}
          value={license?.rejectionNoteForWorker}
          onChange={(e) => {
            handleLicenseUpdate("rejectionNoteForWorker", e.target.value);
          }}
        />
      </Form.Item>
      <Form.Item name="notifyRejection">
        <label htmlFor="switch-rejection" style={{ paddingRight: "12px" }}>
          Send notification to Worker
        </label>
        <Switch
          data-testid="license-rejection-notify-switch"
          aria-label="Send notification to worker"
          disabled={disableForm}
          checked={license?.notifyRejection}
          onChange={(value) => {
            handleLicenseUpdate("notifyRejection", value);
          }}
          id="switch-rejection"
        />
      </Form.Item>
    </Form>
  );
}

export function LicenseForm({
  license,
  qualifications,
  agentId,
  setLicense,
  licenseDetailsMode,
  userHasLicenseManagerRole,
  showFilePreview,
  disableRejectionForm,
}: ILicenseFormProps) {
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  const [form] = Form.useForm();

  const statesListWithAnyOption = useMemo(() => {
    return [anyStateOption, ...stateList];
  }, []);
  useEffect(() => {
    form.setFieldsValue({
      ...license,
      ...(license.expiresAt ? { expiresAt: moment(license.expiresAt) } : {}),
      /* Using moment because date-fns functions return plain JavaScript Date objects,
       * which don't have a clone method. Antd's DatePicker expects a Moment object.
       */
      ...(license.issueDate ? { issueDate: moment.utc(license.issueDate) } : {}),
      state: license?.state || "Any",
    });
  }, [form, license]);

  const handleLicenseUpdate = (key: string, value: boolean | string) => {
    if (!setLicense) {
      return;
    }
    const updatedLicense = {
      ...license,
      [key]: value,
    };

    if (key === "status") {
      // Prevent future expiry date if status is expired

      const dateDiff = moment()
        .startOf("day")
        .diff(moment(license.expiresAt).endOf("day"), "days", true);

      if (license?.expiresAt && dateDiff <= 0 && value === LICENSE_STATUSES.EXPIRED) {
        const yesterday = moment().startOf("day").subtract(1, "days", true);
        updatedLicense.expiresAt = yesterday;
        form.setFieldsValue({
          expiresAt: yesterday,
        });
      }
      // Prevent past expiry date if status is active or pending
      else if (
        license?.expiresAt &&
        dateDiff > 0 &&
        (value === LICENSE_STATUSES.PENDING || value === LICENSE_STATUSES.ACTIVE)
      ) {
        updatedLicense.expiresAt = "";
        form.setFieldsValue({
          expiresAt: "",
        });
      }

      logEvent(ADMIN_EVENTS.LICENSE_STATUS_CHANGED, {
        hcp_id: agentId,
        previous_status: license.status,
        new_status: value,
        days_until_expired: moment(license.expiresAt).diff(moment(), "days"),
      });
    }
    setLicense(updatedLicense);
  };

  const onValuesChange = (changedValues, _allValues) => {
    if (!setLicense) {
      return;
    }
    const updatedLicense = {
      ...license,
      ...changedValues,
    };

    const dateDiff = moment()
      .startOf("day")
      .diff(moment(changedValues?.expiresAt).endOf("day"), "days", true);
    if (
      moment(changedValues?.expiresAt) &&
      dateDiff > 0 &&
      (license.status === LICENSE_STATUSES.PENDING || license.status === LICENSE_STATUSES.ACTIVE)
    ) {
      updatedLicense.status = LICENSE_STATUSES.EXPIRED;
    } else if (
      moment(changedValues?.expiresAt) &&
      dateDiff <= 0 &&
      license.status === LICENSE_STATUSES.EXPIRED
    ) {
      updatedLicense.status = LICENSE_STATUSES.PENDING;
    }
    setLicense(updatedLicense);
  };

  const handleFileInput = async ({ file }) => {
    if (!agentId || !setLicense) {
      showErrorToast("Invalid Worker Id");
      return;
    }
    setUploadingFile(true);
    setLicense({
      ...license,
      fileName: "",
      fileStorageFileKey: "",
    });
    try {
      const fileType = file.type.split("/").pop();
      const { fileStorageFileKey } = await uploadVerificationDoc(agentId, fileType, file);

      setLicense({
        ...license,
        fileStorageFileKey,
        fileName: file.name || fileStorageFileKey,
      });

      logEvent(ADMIN_EVENTS.LICENSE_VERIFICATION_DOCUMENT_CHOSEN, {
        hcp_id: agentId,
        success: true,
        license_status: license.status,
      });
      // FIXME: Automatically disabled during ESLint config update
    } catch {
      showErrorToast("Failed to upload the file");
      logEvent(ADMIN_EVENTS.LICENSE_VERIFICATION_DOCUMENT_CHOSEN, {
        hcp_id: agentId,
        success: false,
        license_status: license.status,
      });
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <Card bordered={false} className="add-license">
      <Row className="add-license-row">
        {showFilePreview && agentId && license.fileStorageFileKey && setLicense && (
          <Col span={12} className="add-license-file-preview-col">
            <LicenseDocPreview
              workerId={agentId}
              fileStorageFileKey={license.fileStorageFileKey}
              isPreviewImage={true}
              height="500px"
              setLicense={setLicense}
              license={license}
            />
          </Col>
        )}
        <Col span={showFilePreview ? 12 : 24} className="add-license-form-col">
          <Form
            {...{ labelCol: { span: 6 }, wrapperCol: { span: 18 } }}
            layout="horizontal"
            name="addLicenseForm"
            form={form}
            requiredMark={false}
            colon={false}
            validateTrigger="onBlur"
            validateMessages={{ required: "" }}
            onValuesChange={onValuesChange}
          >
            <Form.Item label="License Type" name="qualification" rules={[{ required: true }]}>
              <Select
                disabled={licenseDetailsMode}
                style={{ width: "60%" }}
                placeholder={"License Type"}
                data-testid="license-qualification-selector"
                aria-label="Select License Qualification"
                showSearch
              >
                {qualifications
                  .filter(
                    (qualification) => qualification.attributes.name !== NurseWorkerType.NURSE
                  )
                  .map((qualification) => {
                    return (
                      <Option
                        key={qualification.attributes.name}
                        value={qualification.attributes.name}
                      >
                        {qualification.attributes.displayLabel}
                      </Option>
                    );
                  })}
                ;
              </Select>
            </Form.Item>
            <Form.Item label="State" name="state">
              <Select
                showSearch
                style={{ width: "60%" }}
                placeholder="License State"
                disabled={licenseDetailsMode}
                data-testid="license-state-selector"
                aria-label="Select License State"
              >
                {statesListWithAnyOption.map((state) => (
                  <Option key={state.name}>{state.name}</Option>
                ))}
              </Select>
            </Form.Item>
            {!licenseDetailsMode && (
              <Form.Item label="Multi-state" name="multiState" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            )}
            <Form.Item name="number" label="License Number">
              <Input
                disabled={licenseDetailsMode}
                placeholder="License Number"
                data-testid="license-number"
              />
            </Form.Item>
            <Form.Item name="issueDate" label="Issue Date">
              <DatePicker
                disabled={licenseDetailsMode}
                format="YYYY-MM-DD"
                placeholder="YYYY-MM-DD"
                allowClear={true}
              />
            </Form.Item>
            <Form.Item name="expiresAt" label="Expiration">
              <DatePicker
                disabled={licenseDetailsMode}
                format="YYYY-MM-DD"
                placeholder="YYYY-MM-DD"
                allowClear={false}
                disabledDate={(date) => {
                  return !userHasLicenseManagerRole ? moment().diff(date, "days") > 0 : false;
                }}
                data-testid="license-expires-at"
                aria-label="License Expiry Date"
              />
            </Form.Item>
            {!licenseDetailsMode && (
              <LicenseStatusButtonGroup
                selectedStatus={license?.status}
                onSelected={(status) => {
                  handleLicenseUpdate("status", status);
                }}
                disabled={licenseDetailsMode || !userHasLicenseManagerRole}
              />
            )}
            {!licenseDetailsMode && license.status === LICENSE_STATUSES.ACTIVE && (
              <div className="add-license-verification-document">
                <label htmlFor="add-license-verification-id">Website Verification</label>
                <div
                  className="add-license-verification-document-div"
                  id="add-license-verification-id"
                >
                  <Input
                    disabled
                    placeholder="File Name"
                    value={license.fileName || license.fileStorageFileKey}
                    className="add-license-verification-document-input"
                    data-testid="add-license-verification-document-input"
                  />
                  <Upload
                    accept="application/pdf, image/*"
                    customRequest={handleFileInput}
                    showUploadList={false}
                  >
                    <Button type="primary" loading={uploadingFile}>
                      Choose...
                    </Button>
                  </Upload>
                </div>
              </div>
            )}
          </Form>
          {!licenseDetailsMode && license?.status === LICENSE_STATUSES.REJECTED && (
            <RejectionReasonForm
              license={license}
              handleLicenseUpdate={handleLicenseUpdate}
              disableForm={!!disableRejectionForm}
            />
          )}
        </Col>
      </Row>
    </Card>
  );
}

export function AddLicense({
  licenseData,
  qualifications,
  isVisible,
  agentId,
  loadAgentLicenses,
  handleCancel,
  agent,
}) {
  const [license, setLicense] = useState<ICreateLicense>(licenseData ?? LICENSE_DETAILS_INIT);

  const userRoles = useSelector<SessionType, string[]>(
    (state) => state.session.adminProfile.roles || []
  );

  const multiStateLength = useCbhFlag(CbhFeatureFlag.NLC_STATES, {
    defaultValue: {
      active_states: [],
    },
  }).active_states.length;

  const userHasLicenseManagerRole = useMemo(() => {
    return userRoles.includes(EMPLOYEE_ROLES.WORKER_LICENSE_MANAGER);
  }, [userRoles]);

  const showFilePreview = !!license.fileStorageFileKey;

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleUpdateLicense = async (license: ICreateLicense, licenseId: string) => {
    try {
      const licenseUpdateData: IUpdateLicense = {};
      for (const key in license) {
        if (!licenseData || license[key] !== licenseData[key]) {
          licenseUpdateData[key] = license[key];
        }
      }
      licenseUpdateData.status = license.status;
      const formattedLicense = formatLicenseDates(licenseUpdateData, agent.tmz);
      await updateLicense(formattedLicense, licenseId, agentId);
      showSuccessToast("License updated");
      if (
        licenseData?.status !== license.status &&
        license.status === LICENSE_STATUSES.ACTIVE &&
        !agent.isTestAccount
      ) {
        logEvent(ADMIN_EVENTS.WORKER_LICENSE_ACTIVATED, {
          email: agent.email,
        });
      }
      logEvent(ADMIN_EVENTS.LICENSE_UPDATED, {
        hcp_id: agentId,
        previous_status: licenseData?.status,
        new_status: license.status,
        days_until_expired: moment(license.expiresAt).diff(moment(), "days"),
      });
      loadAgentLicenses();
    } catch (error) {
      showErrorToast("Error updating License: " + getErrorMessage(error));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const showConfirmModal = (license: ICreateLicense, licenseId: string) => {
    if (licenseData?.status !== license.status) {
      const alertTitle = STATUS_CHANGE_ALERT_TITLES[license.status];
      const alertContent = getConfirmationModalText(license, multiStateLength);
      confirm({
        title: alertTitle,
        icon: <ExclamationCircleOutlined />,
        content: alertContent,
        okText: LICENSE_STATUSES_VALUE_MAPPING[license.status],
        okButtonProps: {
          danger: ![LICENSE_STATUSES.PENDING, LICENSE_STATUSES.ACTIVE].includes(license.status),
        },
        async onOk() {
          await handleUpdateLicense(license, licenseId);
        },
      });
    } else {
      void handleUpdateLicense(license, licenseId);
    }
  };

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-shadow, react-hooks/exhaustive-deps
  const handleSubmitLicense = async (license: ICreateLicense) => {
    if (license?.fileName) {
      delete license.fileName;
    }

    if (licenseData?.id) {
      showConfirmModal(license, licenseData.id);
      return;
    }
    try {
      const formattedLicense = formatLicenseDates(license, agent.tmz) as ICreateLicense;
      const response = await addLicense(formattedLicense, agentId);
      if (response.duplicate) {
        Modal.confirm({
          title: "This license exists",
          content:
            response.status === LICENSE_STATUSES.ACTIVE
              ? DUPLICATE_LICENSE_INFO_TEXT.ACTIVE
              : DUPLICATE_LICENSE_INFO_TEXT.PENDING,
          cancelText: "Cancel",
          okText: "Make changes",
          onCancel: () => {
            loadAgentLicenses();
          },
        });
        return;
      }
      showSuccessToast("License added");
      if (response.status === LICENSE_STATUSES.ACTIVE && !agent.isTestAccount) {
        logEvent(ADMIN_EVENTS.WORKER_LICENSE_ACTIVATED, {
          email: agent.email,
        });
      }
      logEvent(ADMIN_EVENTS.LICENSE_ADDED, {
        hcp_id: agentId,
        license_manager: userHasLicenseManagerRole,
        days_until_expired: license.expiresAt
          ? moment(license.expiresAt).diff(moment(), "days")
          : "",
      });
      loadAgentLicenses();
    } catch (error) {
      showErrorToast("Error creating License: " + getErrorMessage(error));
    }
  };

  const modalFooter = useMemo(() => {
    let isFormValid = license?.qualification?.length > 0;
    if (license.status === LICENSE_STATUSES.REJECTED) {
      isFormValid = isFormValid && (license?.rejectionReason?.length ?? 0) > 0;
    }
    const footer = [
      <Button key="cancel" onClick={handleCancel}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        onClick={() => handleSubmitLicense(license)}
        disabled={!isFormValid}
        data-testid="license-submit-button"
        aria-label={`${licenseData ? "Update" : "Add"} License`}
      >
        {licenseData ? "Update" : "Add"}
      </Button>,
    ];
    return footer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCancel, handleSubmitLicense, license, licenseData]);

  return (
    <Modal
      title={licenseData ? "Edit License" : "Add License"}
      visible={isVisible}
      onCancel={handleCancel}
      footer={modalFooter}
      centered={true}
      style={{ minWidth: showFilePreview ? "80vw" : "40vw" }}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <LicenseForm
        qualifications={qualifications}
        license={license}
        setLicense={setLicense}
        agentId={agentId}
        licenseDetailsMode={false}
        userHasLicenseManagerRole={userHasLicenseManagerRole}
        showFilePreview={showFilePreview}
        disableRejectionForm={licenseData?.status === LICENSE_STATUSES.REJECTED}
      />
    </Modal>
  );
}
